import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { MutationResponse } from 'global';
import { sendCreditApplicationApi } from '../api';
import { SendOcaLinkVariables } from '../api/types';
import { SendOcaLinkInput, SendOcaLinkSendMethods } from '@trnsact/trnsact-shared-types';

export interface SendFlyerOptions {
  contactDetails: {
    email?: string;
    lastName: string;
    firstName: string;
    mobilePhone?: string;
    businessName?: string;
  };
  equipment?: {
    make?: string;
    year?: string;
    model?: string;
    mileage?: number;
    quantity?: number;
    condition?: string;
    serialVin?: string;
    stockNumber?: string;
    description?: string;
    amountRequested?: number;
  };
  sendMethod: SendOcaLinkSendMethods;
  flyerMeta: {
    listOfInventory?: string[];
    listOfEquipment?: string[];
    finance: {
      rate: number;
      term: number;
      downPayment: number;
    };
  };
  lenderProfileId?: string;
}

interface UseSendFlyerResult {
  sendFlyer: (options: SendFlyerOptions) => Promise<string | null>;
  isLoading: boolean;
}

export const useSendFlyer = (): UseSendFlyerResult => {
  const { showNotification } = useNotifications();

  const [sendOcaLink, { loading: isLoading }] = useMutation<MutationResponse<string>, SendOcaLinkVariables>(
    sendCreditApplicationApi.sendOcaLink
  );

  const sendFlyer = async (options: SendFlyerOptions): Promise<string | null> => {
    try {
      const input: SendOcaLinkInput = {
        contactDetails: options.contactDetails,
        equipment: options.equipment || {},
        sendMethod: options.sendMethod,
        isPromoteFlyer: true,
        flyerMeta: options.flyerMeta,
        lenderProfileId: options.lenderProfileId,
      };

      const { data } = await sendOcaLink({ variables: { input } });

      if (!data?.result?.success) {
        showNotification(data?.result?.error || 'Failed to send flyer', { type: 'error' });
        return null;
      }

      showNotification('Flyer sent successfully', { type: 'success' });
      return data.result.entity || null;
    } catch (error: any) {
      showNotification(error.message || 'Failed to send flyer', { type: 'error' });
      return null;
    }
  };

  return {
    sendFlyer,
    isLoading,
  };
};
